import { useEffect } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";

import LocationField from "components/Field/LocationField";
import { FieldConfig } from "components/Form/types";
import { useArrangement } from "./ArrangementContext";
import { US_STATES } from "./constants";
import type { ShippingAddress } from "./types";

const ADDRESS_REF = "shipping_address";

const addressFieldConfig: FieldConfig = {
  ref: ADDRESS_REF,
  type: "location",
  title: "",
  validations: { required: true },
};

function stateNameFromCode(code: string): string {
  return US_STATES.find((s) => s.code === code)?.name ?? code;
}

function stateCodeFromName(name: string): string {
  return US_STATES.find((s) => s.name === name)?.code ?? "";
}

type LocationValues = {
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  no_unit_confirmed?: boolean;
};

type DeliveryAddressFormProps = {
  onValidityChange?: (valid: boolean) => void;
};

/** Static, non-interactive rendering of the saved delivery address. */
export function DeliveryAddressSummary({
  address,
}: {
  address: ShippingAddress;
}) {
  return (
    <div className="mt-6 space-y-1 rounded-md border border-olive bg-white p-4">
      <h3 className="font-bold text-evergreen">Delivery Address</h3>
      <p className="text-base text-soil">{address.recipientName}</p>
      <p className="text-base text-soil">
        {address.street}
        {address.addressLine2 ? `, ${address.addressLine2}` : ""}
      </p>
      <p className="text-base text-soil">
        {address.city}, {address.state} {address.zip}
      </p>
    </div>
  );
}

export default function DeliveryAddressForm({
  onValidityChange,
}: DeliveryAddressFormProps) {
  const { state, updateFulfillment, updateFulfillmentAddress } =
    useArrangement();
  const address = state.fulfillment.shippingAddress;

  // Typed as FieldValues (not inferred from defaultValues): LocationField
  // takes Control<FieldValues>, and react-hook-form's Control is invariant.
  const methods = useForm<FieldValues>({
    mode: "onChange",
    defaultValues: {
      [ADDRESS_REF]: {
        address_line_1: address.street,
        address_line_2: address.addressLine2,
        city: address.city,
        state: stateNameFromCode(address.state),
        zip: address.zip,
        country: "United States",
        no_unit_confirmed: state.fulfillment.noUnitConfirmed,
      },
    },
  });

  const values = (methods.watch(ADDRESS_REF) ?? {}) as LocationValues;
  const line1 = values.address_line_1 ?? "";
  const line2 = values.address_line_2 ?? "";
  const city = values.city ?? "";
  const stateCode = stateCodeFromName(values.state ?? "");
  const zip = values.zip ?? "";
  const noUnitConfirmed = Boolean(values.no_unit_confirmed);

  // One-way live bridge: react-hook-form owns the inputs; the arrangement
  // context mirrors them. Context seeds the RHF defaults only on mount.
  useEffect(() => {
    if (
      line1 === address.street &&
      line2 === address.addressLine2 &&
      city === address.city &&
      stateCode === address.state &&
      zip === address.zip
    ) {
      return;
    }
    updateFulfillmentAddress({
      street: line1,
      addressLine2: line2,
      city,
      state: stateCode,
      zip,
    });
  }, [
    line1,
    line2,
    city,
    stateCode,
    zip,
    address.street,
    address.addressLine2,
    address.city,
    address.state,
    address.zip,
    updateFulfillmentAddress,
  ]);

  // Mirror the "no apt/unit" confirmation so it survives leaving and
  // revisiting this step (the form itself remounts with fresh defaults).
  useEffect(() => {
    if (noUnitConfirmed !== state.fulfillment.noUnitConfirmed) {
      updateFulfillment({ noUnitConfirmed });
    }
  }, [noUnitConfirmed, state.fulfillment.noUnitConfirmed, updateFulfillment]);

  const { errors } = methods.formState;
  const hasAddressError = Boolean(errors[ADDRESS_REF]);

  useEffect(() => {
    onValidityChange?.(!hasAddressError);
  }, [hasAddressError, onValidityChange]);

  return (
    <div className="mt-6 space-y-4 rounded-md border border-olive bg-white p-4">
      <div>
        <h3 className="font-bold text-evergreen">Delivery Address</h3>
        <p className="text-base text-soil">
          Where should we deliver the cremated remains?
        </p>
      </div>

      {/* Styled to match the LocationField inputs below (placeholder-only,
          compact, cantelope focus) so the card reads as one form. */}
      <input
        id="delivery-recipient"
        type="text"
        autoComplete="shipping name"
        aria-label="Recipient Name"
        className="w-full rounded border p-2 focus:outline focus:outline-2 focus:outline-cantelope"
        placeholder="Recipient Name"
        value={address.recipientName}
        onChange={(e) =>
          updateFulfillmentAddress({ recipientName: e.target.value })
        }
      />

      <FormProvider {...methods}>
        <LocationField field={addressFieldConfig} control={methods.control} />
      </FormProvider>
    </div>
  );
}
